import { XMLParser } from 'fast-xml-parser';
import type { Punto, Bbox } from '../geo/geometria';
import { bboxDePuntos } from '../geo/geometria';
import { descargarGml } from '../catastro/wfs';
import { detectarExcepcion, recolectar, texto, primero, epsgDeSrs, numero, entero } from '../catastro/gml';
import type { CursoAguaFila } from '../db/tipos';

/**
 * Cursos de agua del IGN por el WFS INSPIRE de hidrografia de la IDEE (tema hy-p de la BTN):
 * la misma fuente y capa (`hy-p:Watercourse`) que usa el componente Hidrografia_IGN del plugin
 * de Grasshopper. El servicio permite CORS `*` (comprobado 2026-09-23), sirve GML 3.2 en el
 * huso que se pida y pagina con `count`/`startindex` (deegree). El enlace `next` que devuelve
 * lleva el bbox mal formateado, asi que la paginacion se construye aqui.
 */
export const WFS_HIDRO = 'https://servicios.idee.es/wfs-inspire/hidrografia';
export const CAPA_CURSOS = 'hy-p:Watercourse';
/** Distancia maxima que se mide: mas alla, la parcela queda como "sin agua cerca". */
export const DIST_MAX_AGUA_M = 2000;
const PAGINA = 1000;

export interface CursoAgua {
  id: string;
  nombre: string | null;
  /** localType del IGN: "Curso natural", "Canal"... */
  tipo: string | null;
  /** perennial, seasonal, intermittent, ephemeral (codelist INSPIRE) o null. */
  persistencia: string | null;
  orden: number | null;
  anchoMaxM: number | null;
  longitudM: number | null;
  /** Una o varias polilineas en el huso pedido. */
  lineas: Punto[][];
}

export interface ColeccionCursos {
  registros: CursoAgua[];
  epsg: number | null;
  numberMatched: number | null;
  numberReturned: number | null;
}

export function urlCursosAgua(bbox: Bbox, epsg: number, count = PAGINA, startIndex = 0): string {
  const crs = `urn:ogc:def:crs:EPSG::${epsg}`;
  const b = bbox.map((v) => String(Math.round(v * 1000) / 1000)).join(',');
  return `${WFS_HIDRO}?service=WFS&version=2.0.0&request=GetFeature&typeNames=${encodeURIComponent(CAPA_CURSOS)}`
    + `&srsName=${encodeURIComponent(crs)}&bbox=${encodeURIComponent(`${b},${crs}`)}&count=${count}&startindex=${startIndex}`;
}

const parser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '@_',
  removeNSPrefix: true,
  parseTagValue: false,
  parseAttributeValue: false,
  trimValues: true,
  isArray: (nombre) => ['member', 'featureMember', 'curveMember', 'Watercourse', 'spelling'].includes(nombre),
});

function lineaDePosList(posList: unknown): Punto[] {
  const t = texto(posList);
  if (!t) return [];
  const nums = t.split(/\s+/).filter((s) => s.length).map(Number);
  const linea: Punto[] = [];
  for (let i = 0; i + 1 < nums.length; i += 2) {
    if (Number.isFinite(nums[i]) && Number.isFinite(nums[i + 1])) linea.push([nums[i], nums[i + 1]]);
  }
  if (linea.length && linea[0][0] > 3_000_000 && linea[0][1] < 3_000_000) {
    for (const p of linea) { const x = p[0]; p[0] = p[1]; p[1] = x; }
  }
  return linea;
}

function persistenciaDe(nodo: unknown): string | null {
  const href = texto(primero(nodo, '@_href'));
  if (!href) return null;
  const ultimo = href.split('/').pop() ?? null;
  return ultimo && ultimo.length ? ultimo : null;
}

export function parsearCursosAgua(xml: string): ColeccionCursos {
  const excepcion = detectarExcepcion(xml);
  if (excepcion) throw new Error(`El WFS de hidrografía de la IDEE devolvió una excepción: ${excepcion}`);
  const raiz = parser.parse(xml) as Record<string, unknown>;
  const coleccion = (raiz['FeatureCollection'] ?? raiz) as Record<string, unknown>;
  const registros: CursoAgua[] = [];
  for (const w of recolectar(coleccion, 'Watercourse')) {
    if (typeof w !== 'object' || w === null) continue;
    const n = w as Record<string, unknown>;
    const id = texto(n['@_id']) ?? texto(primero(n['inspireId'], 'localId'));
    if (!id) continue;
    const lineas = recolectar(n['geometry'], 'posList').map(lineaDePosList).filter((l) => l.length >= 2);
    if (lineas.length === 0) continue;
    registros.push({
      id,
      nombre: texto(primero(n['geographicalName'], 'text')),
      tipo: texto(primero(n['localType'], 'LocalisedCharacterString')),
      persistencia: persistenciaDe(n['persistence']),
      orden: entero(primero(n['streamOrder'], 'order')),
      anchoMaxM: numero(primero(n['width'], 'upper')),
      longitudM: numero(n['length']),
      lineas,
    });
  }
  let epsg: number | null = null;
  for (const s of recolectar(coleccion, '@_srsName')) { epsg = epsgDeSrs(s); if (epsg) break; }
  const matched = entero(coleccion['@_numberMatched']);
  const returned = entero(coleccion['@_numberReturned']);
  return { registros, epsg, numberMatched: matched, numberReturned: returned };
}

export interface OpcionesCursos {
  f?: typeof fetch;
  senal?: AbortSignal;
  pagina?: number;
  maxPaginas?: number;
}

/** Descarga todos los cursos de agua de la caja, paginando con startindex. */
export async function descargarCursosAgua(bbox: Bbox, epsg: number, op: OpcionesCursos = {}): Promise<CursoAgua[]> {
  const pagina = op.pagina ?? PAGINA;
  const maxPaginas = op.maxPaginas ?? 50;
  const salida: CursoAgua[] = [];
  const vistos = new Set<string>();
  for (let i = 0; i < maxPaginas; i++) {
    if (op.senal?.aborted) throw new Error('Cancelado');
    const xml = await descargarGml(urlCursosAgua(bbox, epsg, pagina, i * pagina), { f: op.f, reintentos: 2, timeoutMs: 120_000 });
    const { registros, numberReturned } = parsearCursosAgua(xml);
    for (const r of registros) {
      if (vistos.has(r.id)) continue;
      vistos.add(r.id);
      salida.push(r);
    }
    const devueltos = numberReturned ?? registros.length;
    if (devueltos < pagina || registros.length === 0) break;
  }
  return salida;
}

/** Fila de la tabla `hidrografia`: geometria como GeoJSON MultiLineString en el huso, con su caja. */
export function cursoAFila(c: CursoAgua, epsg: number, tesela: string, ahora: number): CursoAguaFila | null {
  const puntos: Punto[] = [];
  for (const l of c.lineas) for (const p of l) puntos.push(p);
  if (puntos.length < 2) return null;
  const [minx, miny, maxx, maxy] = bboxDePuntos(puntos);
  return {
    id: c.id, nombre: c.nombre, tipo: c.tipo, persistencia: c.persistencia, orden: c.orden, anchoMaxM: c.anchoMaxM,
    longitudM: c.longitudM, geom: JSON.stringify({ type: 'MultiLineString', coordinates: c.lineas }), epsg,
    minx, miny, maxx, maxy, tesela, cargadaEn: ahora,
  };
}

/** Texto corto para la interfaz: "Río Guadiaro" o "arroyo sin nombre". */
export function nombreCurso(nombre: string | null, tipo: string | null): string {
  if (nombre) return nombre;
  return tipo && /canal/i.test(tipo) ? 'canal sin nombre' : 'arroyo sin nombre';
}

export const NOMBRE_PERSISTENCIA: Record<string, string> = {
  perennial: 'permanente',
  seasonal: 'estacional',
  intermittent: 'intermitente',
  ephemeral: 'efímero',
};
